import {InvoiceBalance} from '@/invoice/InvoiceBalance'
import {CreateInvoiceCommand, SendAs} from '@/invoice/types/CreateInvoiceCommand'
import {usePanelManager} from '@tritt/multipanel'
import {get, httpDelete, post} from '@/services/infrastructure/api.service'
import emitter from '@/services/infrastructure/emitter'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'
import {FOCUS_TRANS_SUBMIT} from '@/types/events'
import {addDays, differenceInCalendarDays, format} from 'date-fns'
import {INVOICE_CREATOR, INVOICE_VIEW} from './'
import {InvoiceSequence, uninitializedSequence} from '@/invoice/types/InvoiceSequence'
import {calculatedLinePrice, createProductLine, ProductLine, productLineNetPrice, productLineVatAmount} from '@/invoice/InvoiceCreator/invoiceProductLine'
import {computed, ComputedRef, nextTick, ref, watch} from 'vue'
import {PeppolParticipation} from '@/invoice/types/PeppolParticipation'
import {VatSummary} from '@/invoice/types/VatSummary'
import _ from 'lodash'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import {ToastType, usePanelToast} from '@tritt/multipanel'
import {bankAccounts} from '@/bank/bank-account.service'
import {useVatService} from '@/vat/vat.service'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {CUSTOMER_ACCOUNT_NO, INCOME_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {newEntryLine, useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {useSetting} from '@/setting/setting.service'
import {Organization} from '@/organization/Organization'
import {fetchPatronOverview} from '@/services/infrastructure/patron.service'
import {selectedOrg} from '@/tenants/org.service'
import {Invoice, InvoiceDebtCollectionSettings, InvoiceExtendedInfo, InvoiceLine, OutgoingInvoice} from '@/invoice/types/Invoice'
import {KidGenerator} from '@/bank/KidGenerator'
import {defaultIncomeVatOption, defaultVatOption} from '@/vat/types/VatOptions'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {v4 as uuidv4} from 'uuid'

const INVOICE_PATH = '/invoice'
const BALANCE_PATH = '/balance'

const DEBT_COLLECTION_PATH = '/debt-collection'
const INVOICE_SETTINGS_PATH = '/invoice-settings'
const EXTENDED_INVOICE_INFO_PATH = '/extended-invoice-info'

const createOutgoingInvoice = (command: CreateInvoiceCommand): Promise<OutgoingInvoice> => post(INVOICE_PATH + '/outgoing', command)

const getInvoiceSequence = async (): Promise<InvoiceSequence | undefined> => {
    return await get(INVOICE_PATH + '/sequence')
}

const formatInvoiceSequence = (sequence: InvoiceSequence): string => {
    return `${sequence.prefix}${sequence.no}${sequence.suffix}`
}

const initializeSequence = async (sequence: InvoiceSequence) => {
    return post(INVOICE_PATH + '/sequence', sequence)
}

const getInvoice = async (uuid: string): Promise<Invoice | undefined> => {
    return await get<Invoice>(INVOICE_PATH + '/' + uuid)
}

const getPeppolParticipation = async (subAccountUuid?: string): Promise<PeppolParticipation | undefined> => {
    return await get('/peppol/participant', subAccountUuid && {customerUuid: subAccountUuid})
}

const deletePeppolParticipant = async () => {
    return await httpDelete('/peppol/participant')
}

const registerPeppolParticipant = async () => {
    return await post('/peppol/participant', {})
}

const getInvoiceBalancesWithDueDate = async (fromDate?: Date, toDate?: Date): Promise<InvoiceBalance[]> => {
    // TODO cleanup typing
    const invoiceBalances = (await Promise.all([
        get<any[]>(INVOICE_PATH + BALANCE_PATH, {
            fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : null,
            toDate: toDate ? format(toDate, 'yyyy-MM-dd') : null
        }),
        get<any[]>(DEBT_COLLECTION_PATH + EXTENDED_INVOICE_INFO_PATH)
    ])).map(result => result ?? [])

    const balances: InvoiceBalance[] = invoiceBalances[0].map((invoiceBalance: InvoiceBalance) => {
        const extendedInvoiceInfo = invoiceBalances[1].find((info: any) => info.invoiceUuid === invoiceBalance.uuid)
        if (extendedInvoiceInfo) {
            invoiceBalance.debtCollectionId = extendedInvoiceInfo.externalId
            invoiceBalance.debtCollectionStatus = extendedInvoiceInfo.status
        }
        return invoiceBalance
    })

    return balances.map(inv => {
        let daysBetween
        if (inv.balance === 0)
            daysBetween = Number.MAX_VALUE
        else {

            const today = new Date()
            const dueDate = new Date(inv.dueDate)

            daysBetween = differenceInCalendarDays(dueDate, today) || 0
        }

        return {
            ...inv,
            dueDays: daysBetween,
            debtCollectionStatus: inv.debtCollectionStatus ? inv.debtCollectionStatus : 'Ikke sendt'
        }
    })
}

const getInvoiceBalance = async (uuid: string) => {
    return await get(INVOICE_PATH + '/' + uuid + BALANCE_PATH)
}

export const getDebtCollectionSettings = async (invoiceUuid: string): Promise<InvoiceDebtCollectionSettings | undefined> => {
    return await get<InvoiceDebtCollectionSettings>(DEBT_COLLECTION_PATH + INVOICE_SETTINGS_PATH, {invoiceUuid})
}

export const getExtendedInvoiceInfo = async (invoiceUuid: string): Promise<InvoiceExtendedInfo | undefined> => {
    return await get<InvoiceExtendedInfo>(`${DEBT_COLLECTION_PATH + EXTENDED_INVOICE_INFO_PATH}/${invoiceUuid}`)
}

export const setDebtCollectionSettings = async (request: any) => {
    return await post(DEBT_COLLECTION_PATH + INVOICE_SETTINGS_PATH, request)
}

export const syncPastDueInvoicesToDebtCollection = async () => {
    return await get(DEBT_COLLECTION_PATH + '/manual-sync')
}

export const syncExternallyUpdatedInvoices = async () => {
    return await get(DEBT_COLLECTION_PATH + '/sync-updates')
}

export const useInvoiceService = () => {

    const {setPanelPath} = usePanelManager()
    const {reset: resetCreate, transactionEntryLines} = useTransactionCreator()

    const updateSingleTransactionEntryLine = (subAccountLine: SubAccountLine | InvoiceBalance) => {
        resetCreate()
        transactionEntryLines.value[0] = createSingleTransactionEntryLine(subAccountLine)
    }

    const createSingleTransactionEntryLine = (subAccountLine: SubAccountLine | InvoiceBalance) => {
        const subAccount = findSubAccountByUuid(subAccountLine.subAccountUuid)
        const subAccountNo = subAccount?.relationType === CUSTOMER ? CUSTOMER_ACCOUNT_NO : SUPPLIER_ACCOUNT_NO

        const transactionEntryLine = newEntryLine()

        // A bit nasty any better ways to do this?
        const remainingAmount = 'remainingAmount' in subAccountLine ? subAccountLine.remainingAmount : subAccountLine.balance
        const invoiceUuid = 'invoiceUuid' in subAccountLine ? subAccountLine.invoiceUuid : subAccountLine.uuid
        const subAccountLineUuid = 'subAccountLineUuid' in subAccountLine ? subAccountLine.subAccountLineUuid : subAccountLine.uuid

        if (remainingAmount > 0) {
            transactionEntryLine.debitAccount = '1920'
            transactionEntryLine.debitSubAccount = undefined
            transactionEntryLine.creditAccount = subAccountNo
            transactionEntryLine.creditSubAccount = subAccountLine.subAccountUuid
        } else {
            transactionEntryLine.creditAccount = '1920'
            transactionEntryLine.creditSubAccount = undefined
            transactionEntryLine.debitAccount = subAccountNo
            transactionEntryLine.debitSubAccount = subAccountLine.subAccountUuid
        }

        if (subAccountLine.dueDate) {
            transactionEntryLine.date = new Date(subAccountLine.dueDate)
        }

        transactionEntryLine.invoiceNo = subAccountLine.invoiceNo
        transactionEntryLine.invoiceUuid = invoiceUuid
        transactionEntryLine.kid = subAccountLine.kid
        transactionEntryLine.bban = subAccount.bban ?? subAccount.iban
        transactionEntryLine.amount = Math.abs(remainingAmount)
        transactionEntryLine.subAccountLines.push(subAccountLineUuid)

        transactionEntryLine.description = remainingAmount > 0 ? `Received payment for faktura ${subAccountLine.invoiceNo}` : `Sent payment for faktura ${subAccountLine.invoiceNo}`

        return transactionEntryLine
    }

    const payInvoice = async (subAccountLine: SubAccountLine | InvoiceBalance, panelId: number) => {
        await setPanelPath({...CREATE_TRANSACTION, panelId}, false)

        updateSingleTransactionEntryLine(subAccountLine)

        await nextTick(() => {
            emitter.emit(FOCUS_TRANS_SUBMIT)
        })
    }

    const creditInvoiceV2 = async (invoice: InvoiceBalance, invoiceLines: InvoiceLine[], panelId: number, creditLine?: string) => {
        await setPanelPath({...INVOICE_CREATOR, panelId})
        const {findVatOptionFromCode} = useVatService()
        const {invoiceData} = useInvoiceCreator()
        if (creditLine != null) {
            creditSubAccountLine.value = creditLine
        }
        invoiceData.value.customer = findSubAccountByUuid(invoice.subAccountUuid)
        invoiceData.value.customerReference = 'Kred. Fakturanr.:' + invoice.invoiceNo
        invoiceData.value.invoiceDate = new Date(invoice.date)
        invoiceData.value.dueDate = new Date(invoice.dueDate)
        invoiceData.value.description = 'Kreditering av faktura ' + invoice.invoiceNo
        invoiceData.value.productLines = invoiceLines
            .map(il => {
                const pl = createProductLine()
                pl.productUuid = il.productUuid!
                pl.productName = `Kreditering: ${il.description}`
                pl.customDescription = `Kreditering: ${il.description}` //Not in use? Deprecate
                pl.bookkeepingAccount = il.accountNo || INCOME_ACCOUNT_NO
                pl.unitPrice = il.unitPrice
                pl.quantity = il.quantity * -1
                pl.discount = il.discount
                pl.vatOption = findVatOptionFromCode(il.vatCode) ?? defaultIncomeVatOption
                return pl
            })
    }

    const creditInvoice = async (invoice: InvoiceBalance, transactionLines: TransactionLine[], panelId: number, creditLine?: string) => {
        await setPanelPath({...INVOICE_CREATOR, panelId})
        const {incomeVatOptions} = useVatService()
        const {invoiceData} = useInvoiceCreator()
        if (creditLine != null) {
            creditSubAccountLine.value = creditLine
        }
        invoiceData.value.customer = findSubAccountByUuid(invoice.subAccountUuid)
        invoiceData.value.invoiceDate = new Date(invoice.date)
        invoiceData.value.dueDate = new Date(invoice.dueDate)
        invoiceData.value.productLines = transactionLines
            .filter(tl => tl.accountNo === INCOME_ACCOUNT_NO)
            .map(tl => {
                const pl = createProductLine()
                pl.productName = `Kreditering: ${tl.description}`
                pl.unitPrice = tl.amount * -1
                pl.quantity = -1
                pl.vatOption = incomeVatOptions.value.find((vatOption: any) => vatOption.id === tl.vatOptionId) ?? defaultIncomeVatOption
                return pl
            })
    }


    return {payInvoice, creditInvoice, updateSingleTransactionEntryLine, creditInvoiceV2}
}


export const calculateNetSum = (productLines: ProductLine[]): number => {
    return productLines.reduce((acc, pl) => acc + productLineNetPrice(pl), 0)
}

export const calculateTotalVatAmount = (productLines: ProductLine[]): number => {
    return productLines.reduce((acc, pl) => acc + productLineVatAmount(pl), 0)
}

export const calculateTotal = (productLines: ProductLine[]): number => {
    return productLines.reduce((acc, pl) => acc + calculatedLinePrice(pl), 0)
}

const showProject = ref(false)
const showDepartment = ref(false)
const showDiscount = ref(false)
const defaultDueDate = ref(14)
const isWorking = ref(false)
const creditSubAccountLine = ref('')
const nextInvoiceNo = ref<InvoiceSequence>()
const initialInvoiceNo = ref(1000)
const peppolParticipation = ref<PeppolParticipation>()
const organization = ref<Organization | undefined>()

export type Address = {
    addressLine?: string
    postalCode: string
    postalLocation: string
    countryCode: string
}

const uploadedDocMetas = ref<DocumentMeta[]>([])

export type InvoiceData = {
    customer: SubAccount | undefined,
    customerReference: string,
    supplierReference: string,
    deliveryDate: Date,
    deliveryLocation: Address,
    invoiceDate: Date,
    dueDate: Date,
    incomeAccountNo: string,
    description: string,
    productLines: ProductLine[],
    amount: number | undefined,
}

const defaultDeliveryLocation: Address = {
    postalCode: '',
    postalLocation: '',
    countryCode: 'NO'
}


const invoiceData = ref<InvoiceData>(
    {
        customer: undefined,
        customerReference: 'Kunderef.',
        deliveryDate: new Date(),
        deliveryLocation: {...defaultDeliveryLocation},
        description: '',
        dueDate: addDays(new Date(), defaultDueDate.value),
        incomeAccountNo: '1920',
        invoiceDate: new Date(),
        supplierReference: '',
        productLines: [createProductLine()],
        amount: undefined
    }
)

const dueDateTouched = ref(false)
const deliveryDateTouched = ref(false)
const onUpdateInvoiceDate = (date: Date) => {
    if (!dueDateTouched.value)
        invoiceData.value.dueDate = addDays(date, defaultDueDate.value)
    if (!deliveryDateTouched.value)
        invoiceData.value.deliveryDate = date
}
watch(() => invoiceData.value.invoiceDate, onUpdateInvoiceDate)


const customer = computed<SubAccount | undefined>({
        get: () => {
            return invoiceData.value.customer
        },
        set: async (newCustomer: SubAccount | undefined) => {
            invoiceData.value.customer = newCustomer

            if (newCustomer?.address) {
                invoiceData.value.deliveryLocation = { ...defaultDeliveryLocation, ...newCustomer.address } as Address
            }

            if (newCustomer && newCustomer.isBusiness) {
                peppolParticipation.value = await getPeppolParticipation(newCustomer.uuid)
            }

            invoiceData.value.customerReference = newCustomer?.contactInfo?.name || newCustomer?.name || ''
            invoiceData.value.supplierReference = selectedOrg.value?.organizationName || 'Vår ref.'
        }
    }
)

const clear = () => {
    nextInvoiceNo.value = undefined
    customer.value = undefined
    invoiceData.value.invoiceDate = new Date()
    invoiceData.value.dueDate = addDays(new Date(), defaultDueDate.value)
    invoiceData.value.description = ''
    invoiceData.value.productLines = [createProductLine()]
    peppolParticipation.value = undefined
    invoiceData.value.customerReference = ''
    invoiceData.value.supplierReference = ''
    creditSubAccountLine.value = ''
    invoiceData.value.deliveryDate = new Date()
    invoiceData.value.deliveryLocation = { ...defaultDeliveryLocation }
    uploadedDocMetas.value = []
}

const clearCustomer = () => {
    customer.value = undefined
}

const updateProductLineAtIndex = (productLine: ProductLine, index: number) => {
    invoiceData.value.productLines[index] = productLine
}


const deliveryLocation = computed<Address>({
    get: () => invoiceData.value.deliveryLocation,
    set: (val: Address) => { invoiceData.value.deliveryLocation = val },
})

export const useInvoiceCreator = () => {
    const {showPanelToast} = usePanelToast()
    const {setting: vatLiableSetting} = useSetting<boolean>('VAT_LIABLE')
    const {setting: defaultInvoiceDueDateSetting} = useSetting<number>('DEFAULT_INVOICE_DUE_DATE')
    const {setting: defaultIncomingPaymentAccountSetting} = useSetting<string>('DEFAULT_INCOMING_PAYMENT_ACCOUNT')
    const {setting: invoiceSupplierPhoneSetting } = useSetting<string>('INVOICE_SUPPLIER_PHONE')
    const {setting: invoiceSupplierEmailSetting } = useSetting<string>('INVOICE_SUPPLIER_EMAIL')
    const patron = ref({email: undefined, phone: undefined})


    const isVatLiable: ComputedRef<Boolean> = computed<Boolean>(() => {
        const isVatLiableSetting = vatLiableSetting.value
        return isVatLiableSetting?.value ?? isVatLiableSetting?.default ?? isVatLiable.value
    })

    const productLinesNetSum = computed<number>(() => {
        return calculateNetSum(invoiceData.value.productLines)
    })

    const productLinesVatAmount = computed<number>(() => {
        return calculateTotalVatAmount(invoiceData.value.productLines)
    })

    const productLinesTotal = computed<number>(() => {
        return calculateTotal(invoiceData.value.productLines)
    })

    const vatSummaries = computed<VatSummary[]>(() => {
        const linesGroupedByVatRate = _.groupBy(invoiceData.value.productLines, 'vatOption.rate')
        return _.map(linesGroupedByVatRate, (lines, vatRate) => {
            const baseAmount = _.sumBy(lines, line => productLineNetPrice(line))
            const vatAmount = _.sumBy(lines, line => productLineVatAmount(line))
            return {
                vatRate: Number(vatRate),
                baseAmount,
                vatAmount
            }
        })
    })

    const bankAccount = computed(() => bankAccounts.value.find(ba => ba.bookkeepingAccountNo === invoiceData.value.incomeAccountNo))

    const isKidEnabled = computed(() => {
        return bankAccount.value?.kidSettings && customer.value?.subAccountNo
    })

    const kid = computed(() => {
        if (isKidEnabled.value) {
            return new KidGenerator(bankAccount.value!.kidSettings!).generate(customer.value!.subAccountNo.toString(), !nextInvoiceNo.value ? initialInvoiceNo.value.toString() : formatInvoiceSequence(nextInvoiceNo.value))
        }
        return undefined
    })

    const addProductLine = (index: number | undefined = undefined, productLine: ProductLine | undefined = undefined) => {
        if (!productLine) {
            productLine = createProductLine()
        }
        if (index !== undefined) {
            invoiceData.value.productLines.splice(index, 0, productLine)
            return
        } else {
            invoiceData.value.productLines.push(productLine)
        }
    }

    const copyLineAtIndex = (index: number) => {
        const lineToCopy = invoiceData.value.productLines[index]
        const copiedLine = {
            ...lineToCopy,
            tempUuid: uuidv4()
        }
        addProductLine(index + +1, copiedLine)
    }

    const deleteLineAtIndex = (index: number) => {
        invoiceData.value.productLines.splice(index, 1)
        if (invoiceData.value.productLines.length === 0) {
            addProductLine()
        }
    }

    const isCreditNote = computed(() => {
        return productLinesTotal.value < 0
    })

    const canReceiveInvoice = computed(() => {
        return peppolParticipation.value?.canReceiveInvoices
    })

    const canReceiveCreditNote = computed(() => {
        return peppolParticipation.value?.canReceiveCreditNotes
    })

    const fetchNextInvoiceNo = async () => {
        const sequence = await getInvoiceSequence()
        nextInvoiceNo.value = sequence ? sequence : uninitializedSequence
    }

    const fetchDefaultDueDate = () => {
        const setting = defaultInvoiceDueDateSetting.value
        defaultDueDate.value = setting?.value || setting?.default || 14
        invoiceData.value.dueDate = addDays(new Date(), defaultDueDate.value)
    }

    const fetchIsVatLiable = () => {
        const setting = vatLiableSetting.value
        if (!(setting?.value ?? setting?.default ?? true)) {
            invoiceData.value.productLines.forEach((pl: ProductLine) => pl.vatOption = defaultVatOption)
        }
    }

    const initInvoiceCreator = async () => {
        if (selectedOrg.value?.organizationId) {
            patron.value = await fetchPatronOverview(selectedOrg.value.organizationId)

        }
        await fetchNextInvoiceNo()
        fetchDefaultDueDate()
        fetchIsVatLiable()
        invoiceData.value.incomeAccountNo = defaultIncomingPaymentAccountSetting.value?.value ?? defaultIncomingPaymentAccountSetting.value?.default ?? '1920'
        showDepartment.value = !!departmentRequired.value.value
        showProject.value = !!projectRequired.value.value
    }

    const senderPhoneNo = computed(() => {
        return invoiceSupplierPhoneSetting.value.value || patron.value?.phone
    })
    const senderEmail = computed(() => {
        return invoiceSupplierEmailSetting.value.value || patron.value?.email
    })

    const {setting: departmentRequired} = useSetting<boolean | null>('DEPARTMENT_REQUIRED')
    const {setting: projectRequired} = useSetting<boolean | null>('PROJECT_REQUIRED')

    const valid = computed(() => validationErrors.value.length === 0)

    // TODO cleanup validation
    const validationErrors = computed<string[]>(() => {
            const errors = []
            if (!invoiceData.value.customer) {
                errors.push('invoice.validation.customer.blank')
            }
            if (invoiceData.value.productLines.some((pl: ProductLine) => pl.unitPrice === 0)) {
                errors.push('invoice.validation.product-line.zero')
            }
            if (invoiceData.value.productLines.some((pl: ProductLine) => !pl.productName)) {
                errors.push('invoice.validation.product-line.no-description')
            }
            if (!isCreditNote.value && (!invoiceData.value.incomeAccountNo || !bankAccounts.value.find(ba => ba.bookkeepingAccountNo === invoiceData.value.incomeAccountNo))) {
                errors.push('invoice.validation.ehf.missing-bank-account')
            }

            if (!deliveryLocation.value.postalLocation || !deliveryLocation.value.postalCode || deliveryLocation.value.countryCode.length !== 2) {
                errors.push('invoice.validation.deliveryLocation.missing')
            }
            if (!invoiceData.value.customerReference || invoiceData.value.customerReference.trim().length === 0) {
                errors.push('invoice.validation.customer.no-reference')
            }
            if (departmentRequired.value.value && invoiceData.value.productLines.some((pl: ProductLine) => !pl.department)) {
                errors.push('invoice.validation.product-line.no-department')
            }
            if (projectRequired.value.value && invoiceData.value.productLines.some((pl: ProductLine) => !pl.project)) {
                errors.push('invoice.validation.product-line.no-project')
            }
            const projectIds = new Set(
                invoiceData.value.productLines
                    .map((pl: ProductLine) => pl.project)
                    .filter((v): v is string => typeof v === 'string' && v.length > 0)
            )

            if (projectIds.size > 1) {
                errors.push('invoice.validation.product-line.multiple-projects')
            }

            const departmentIds = new Set(
                invoiceData.value.productLines
                    .map((pl: ProductLine) => pl.department)
                    .filter((v): v is string => typeof v === 'string' && v.length > 0)
            )

            if (departmentIds.size > 1) {
                errors.push('invoice.validation.product-line.multiple-departments')
            }
            return errors
        }
    )

    const createInvoiceCmd = computed<Omit<CreateInvoiceCommand, 'send'>>(() => ({
        customerReference: invoiceData.value.customerReference,
        supplierReference: invoiceData.value.supplierReference,
        date: format(invoiceData.value.invoiceDate, 'yyyy-MM-dd'),
        description: invoiceData.value.description,
        dueDate: format(invoiceData.value.dueDate, 'yyyy-MM-dd'),
        bankAccountNo: bankAccount.value?.bban ?? bankAccount.value?.iban ?? '',
        productLines: invoiceData.value.productLines.map((pl: ProductLine) => ({
            productUuid: pl.productUuid,
            productName: pl.productName,
            customDescription: pl.customDescription,
            unitPrice: pl.unitPrice,
            quantity: pl.quantity,
            discount: pl.discount,
            vatOptionId: pl.vatOption.id,
            bookkeepingAccountNo: pl.bookkeepingAccount,
            project: pl.project,
            department: pl.department
        })),
        senderPhoneNo: senderPhoneNo.value,
        senderEmail: senderEmail.value,
        subAccountUuid: invoiceData.value.customer!.uuid,
        creditSubAccountLine: creditSubAccountLine.value,
        deliveryDate: format(invoiceData.value.deliveryDate, 'yyyy-MM-dd'),
        deliveryLocation: deliveryLocation.value,
        attachmentUuids: uploadedDocMetas.value.map(dm => dm.uuid)
    }))

    const create = async (send: SendAs) => {
        const cmd: CreateInvoiceCommand = {
            ...createInvoiceCmd.value,
            send
        }

        isWorking.value = true
        if (!nextInvoiceNo.value || nextInvoiceNo.value.uninitialized) {
            try {
                await initializeSequence({prefix: '', no: initialInvoiceNo.value, suffix: ''})
            } catch (e) {
                nextInvoiceNo.value = await getInvoiceSequence()
                if (!nextInvoiceNo.value) {
                    throw new Error('Kunne ikke hente neste fakturanummer')
                }
            }
        }

        try {
            const result = await createOutgoingInvoice(cmd)
            emitter.emit(TRANSACTION_CREATED)
            await clearAndSetInvoiceNo()
            isWorking.value = false
            showPanelToast('Ny faktura opprettet: ' + result.invoiceNo, {...INVOICE_VIEW, queryValues: {invoiceUuid: result.uuid}}, ToastType.SUCCESS, 30000)
        } catch (e) {
            showPanelToast('Oppretting av faktura feilet', undefined, ToastType.ERROR, 10000)
            window.TrackJS && window.TrackJS.console.error('Create invoice failed with cmd %d', cmd, e)
        } finally {
            isWorking.value = false
        }
    }

    const clearAndSetInvoiceNo = async () => {
        clear()
        await fetchNextInvoiceNo()
    }

    return {
        createInvoiceCmd,
        invoiceData,
        isVatLiable,
        deliveryLocation,
        customer,
        clearCustomer,
        showProject,
        showDepartment,
        showDiscount,
        productLinesNetSum,
        updateProductLineAtIndex,
        productLinesVatAmount,
        productLinesTotal,
        vatSummaries,
        organization,
        isWorking,
        nextInvoiceNo,
        initialInvoiceNo,
        peppolParticipation,
        canReceiveCreditNote,
        canReceiveInvoice,
        valid,
        validationErrors,
        addProductLine,
        copyLineAtIndex,
        deleteLineAtIndex,
        isCreditNote,
        create,
        clear,
        clearAndSetInvoiceNo,
        initInvoiceCreator,
        bankAccount,
        senderPhoneNo,
        senderEmail,
        kid,
        isKidEnabled,
        projectRequired,
        departmentRequired,
        dueDateTouched,
        deliveryDateTouched,
        uploadedDocMetas
    }
}

export {
    createOutgoingInvoice, getInvoice,
    getPeppolParticipation, deletePeppolParticipant, registerPeppolParticipant,
    initializeSequence, getInvoiceSequence, formatInvoiceSequence, getInvoiceBalance, getInvoiceBalancesWithDueDate
}

